import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Select from "react-select";
import {
  Button,
  Modal,
  ModalBody,
  Input,
  Label,
  FormGroup,
  Form,
  Row,
  Col,
} from "reactstrap";
import { toast } from "react-toastify";
import * as Yup from "yup";
import {
  createNotificationSettings,
  fetchNotificationSettingsForOptions,
  getNotificationSettingsById,
  updateNotificationSettings,
} from "model/NotificationSettingsModel";
import { fetchCommonStatusList } from "model/UserModel";

const SEND_TO_FIELDS = [
  { name: "toCandidate", label: "Candidate" },
  { name: "toClient", label: "Client" },
  { name: "toCaller", label: "Caller" },
  { name: "toTeamLead", label: "Team Lead" },
  { name: "toAccountExecutive", label: "Account Executive" },
  { name: "toManager", label: "Manager" },
  { name: "toSuperAdmin", label: "Admin" },
];

const notificationSettingsSchema = Yup.object().shape({
  settingsFor: Yup.string().trim().required("Settings for is required"),
  settingsStatusId: Yup.string().trim().required("Status is required"),
});

const unwrapArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  const root = payload?.data ?? payload;
  if (Array.isArray(root)) return root;
  const nested = root?.records ?? root?.rows ?? root?.items;
  return Array.isArray(nested) ? nested : [];
};

const mapNotificationTypeOptions = (payload) =>
  unwrapArray(payload)
    .map((item) => {
      const value = String(
        item?.value ?? item?.id ?? item?.code ?? item?.key ?? item?.name ?? ""
      ).trim();
      if (!value) return null;
      const label =
        item?.label ??
        item?.display ??
        item?.name ??
        item?.title ??
        value;
      return { value, label: String(label) };
    })
    .filter(Boolean);

const mapStatusOptions = (payload) =>
  unwrapArray(payload)
    .map((item) => {
      const value = String(item?.id ?? item?.value ?? item?.code ?? "").trim();
      if (!value) return null;
      const label = String(
        item?.label ??
          item?.display ??
          item?.name ??
          item?.title ??
          value
      ).trim();
      return { value, label };
    })
    .filter(Boolean);

const buildInitialFormState = () => ({
  id: "",
  settingsFor: "",
  toCandidate: false,
  toClient: false,
  toCaller: false,
  toTeamLead: false,
  toAccountExecutive: false,
  toManager: false,
  toSuperAdmin: false,
  settingsStatusId: "",
});

const normalizeDetailToForm = (data = {}) => ({
  id: String(data?.id ?? data?._id ?? "").trim(),
  settingsFor: String(data?.settingsFor ?? "").trim(),
  toCandidate: Boolean(data?.toCandidate),
  toClient: Boolean(data?.toClient),
  toCaller: Boolean(data?.toCaller),
  toTeamLead: Boolean(data?.toTeamLead),
  toAccountExecutive: Boolean(data?.toAccountExecutive),
  toManager: Boolean(data?.toManager),
  toSuperAdmin: Boolean(data?.toSuperAdmin),
  settingsStatusId: String(data?.settingsStatusId ?? "").trim(),
});

const buildSubmitPayload = (formData = {}) => ({
  id: String(formData?.id ?? "").trim(),
  settingsFor: String(formData?.settingsFor ?? "").trim(),
  toCandidate: Boolean(formData?.toCandidate),
  toClient: Boolean(formData?.toClient),
  toCaller: Boolean(formData?.toCaller),
  toTeamLead: Boolean(formData?.toTeamLead),
  toAccountExecutive: Boolean(formData?.toAccountExecutive),
  toManager: Boolean(formData?.toManager),
  toSuperAdmin: Boolean(formData?.toSuperAdmin),
  settingsStatusId: String(formData?.settingsStatusId ?? "").trim(),
});

const AddEditNotificationSettingsModal = ({
  notificationSettingsAddEditModalOpen,
  setNotificationSettingsAddEditModalOpen,
  itemId = null,
  getNotificationSettingsList,
}) => {
  const [formKey, setFormKey] = useState(0);
  const [formData, setFormData] = useState(buildInitialFormState);
  const [errors, setErrors] = useState({});
  const [settingsForOptions, setSettingsForOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const isEditMode = Boolean(String(itemId ?? "").trim());

  const activeStatusOption = useMemo(
    () =>
      statusOptions.find((option) =>
        String(option?.label ?? "").trim().toLowerCase().includes("active")
      ) ?? statusOptions[0],
    [statusOptions]
  );

  const deactiveStatusOption = useMemo(
    () =>
      statusOptions.find((option) => {
        const label = String(option?.label ?? "").trim().toLowerCase();
        return label.includes("deactive") || label.includes("inactive");
      }) ?? statusOptions[1],
    [statusOptions]
  );

  const selectedSettingsForOption = useMemo(
    () =>
      settingsForOptions.find(
        (option) =>
          String(option?.value ?? "") === String(formData?.settingsFor ?? "")
      ) ?? null,
    [settingsForOptions, formData.settingsFor]
  );

  const closeModal = () => {
    setNotificationSettingsAddEditModalOpen(false);
    setErrors({});
    setFormData(buildInitialFormState());
  };

  const handleChange = async (event) => {
    const { name, type, checked, value } = event.target;
    const fieldValue = type === "checkbox" ? checked : value;

    setFormData((previous) => ({
      ...previous,
      [name]: fieldValue,
    }));

    if (!notificationSettingsSchema.fields[name]) return;

    try {
      await Yup.reach(notificationSettingsSchema, name).validate(fieldValue);
      setErrors((previous) => ({ ...previous, [name]: "" }));
    } catch (error) {
      setErrors((previous) => ({ ...previous, [name]: error.message }));
    }
  };

  const handleStatusRadioChange = (statusId) => {
    setFormData((previous) => ({
      ...previous,
      settingsStatusId: String(statusId ?? "").trim(),
    }));
    setErrors((previous) => ({ ...previous, settingsStatusId: "" }));
  };

  const handleSettingsForChange = async (selectedOption) => {
    const fieldValue = String(selectedOption?.value ?? "").trim();

    setFormData((previous) => ({
      ...previous,
      settingsFor: fieldValue,
    }));

    try {
      await Yup.reach(notificationSettingsSchema, "settingsFor").validate(fieldValue);
      setErrors((previous) => ({ ...previous, settingsFor: "" }));
    } catch (error) {
      setErrors((previous) => ({ ...previous, settingsFor: error.message }));
    }
  };

  const loadDropdownOptions = async () => {
    const [settingsForRes, statusRes] = await Promise.all([
      fetchNotificationSettingsForOptions(),
      fetchCommonStatusList(),
    ]);
    setSettingsForOptions(mapNotificationTypeOptions(settingsForRes?.data ?? settingsForRes));
    setStatusOptions(mapStatusOptions(statusRes?.data ?? statusRes));
  };

  const loadDetailById = async (id) => {
    setLoadingDetail(true);
    try {
      const response = await getNotificationSettingsById(id);
      const data = response?.data ?? response ?? {};
      setFormData(normalizeDetailToForm(data));
      setFormKey((key) => key + 1);
    } catch (error) {
      toast.error(error?.message || "Unable to load notification settings");
    } finally {
      setLoadingDetail(false);
    }
  };

  useEffect(() => {
    if (!notificationSettingsAddEditModalOpen) return;

    loadDropdownOptions();

    if (itemId) {
      loadDetailById(itemId);
      return;
    }

    setFormData(buildInitialFormState());
    setFormKey((key) => key + 1);
  }, [notificationSettingsAddEditModalOpen, itemId]);

  useEffect(() => {
    if (!notificationSettingsAddEditModalOpen || formData.settingsStatusId) return;
    if (activeStatusOption?.value) {
      setFormData((previous) => ({
        ...previous,
        settingsStatusId: String(activeStatusOption.value),
      }));
    }
  }, [
    notificationSettingsAddEditModalOpen,
    activeStatusOption,
    formData.settingsStatusId,
  ]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      await notificationSettingsSchema.validate(formData, { abortEarly: false });
      setErrors({});
    } catch (error) {
      const nextErrors = {};
      error.inner?.forEach((item) => {
        nextErrors[item.path] = item.message;
      });
      setErrors(nextErrors);
      return;
    }

    const payload = buildSubmitPayload(formData);
    const response = isEditMode
      ? await updateNotificationSettings(itemId, payload)
      : await createNotificationSettings(payload);

    if (response?.success !== false && (response?.success || response?.isSuccess)) {
      toast.success(response?.message || "Notification settings saved successfully");
      closeModal();
      getNotificationSettingsList?.();
      return;
    }

    toast.error(response?.message || "Unable to save notification settings");
  };

  return (
    <Modal
      isOpen={notificationSettingsAddEditModalOpen}
      toggle={closeModal}
      backdrop="static"
      className="modal-full-height"
      centered
    >
      <ModalBody
        key={formKey}
        className="p-4"
        style={{ maxHeight: "95vh", overflowY: "auto" }}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="fw-bold mb-0 text-uppercase">
            {isEditMode ? "Update" : "Create"}
          </h5>
          <Button close onClick={closeModal} />
        </div>
        <hr />

        {loadingDetail ? (
          <div className="py-5 text-center text-muted">Loading...</div>
        ) : (
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label>
                Settings For <span className="text-danger">*</span>
              </Label>
              <Select
                options={settingsForOptions}
                className={`select2-selection${errors.settingsFor ? " is-invalid" : ""}`}
                placeholder="Select Settings For"
                value={selectedSettingsForOption}
                onChange={handleSettingsForChange}
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  menu: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
              {errors.settingsFor ? (
                <small className="text-danger">{errors.settingsFor}</small>
              ) : null}
            </FormGroup>

            <FormGroup>
              <Label>Send To</Label>
              <Row className="g-2">
                {SEND_TO_FIELDS.map((field) => {
                  const isChecked = Boolean(formData[field.name]);
                  return (
                  <Col md="6" key={field.name}>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <Input
                        key={`notification-send-to-${field.name}-${isChecked}-${formKey}`}
                        type="checkbox"
                        id={`notification-send-to-${field.name}`}
                        name={field.name}
                        className="form-check-input"
                        defaultChecked={isChecked}
                        onChange={handleChange}
                      />
                      <Label
                        for={`notification-send-to-${field.name}`}
                        className="mb-0"
                      >
                        {field.label}
                      </Label>
                    </div>
                  </Col>
                  );
                })}
              </Row>
            </FormGroup>

            <FormGroup>
              <Label>
                Status <span className="text-danger">*</span>
              </Label>
              <div className="input-box d-flex align-items-center justify-content-between">
                <span className="text-muted">Type</span>
                <div>
                  <Input
                    key={`notification-status-active-${formData.settingsStatusId}-${formKey}`}
                    type="radio"
                    name="settingsStatusId"
                    defaultChecked={
                      String(formData.settingsStatusId) ===
                      String(activeStatusOption?.value ?? "")
                    }
                    onChange={() =>
                      handleStatusRadioChange(activeStatusOption?.value)
                    }
                  />{" "}
                  Active
                  <Input
                    key={`notification-status-deactive-${formData.settingsStatusId}-${formKey}`}
                    type="radio"
                    name="settingsStatusId"
                    defaultChecked={
                      String(formData.settingsStatusId) ===
                      String(deactiveStatusOption?.value ?? "")
                    }
                    onChange={() =>
                      handleStatusRadioChange(deactiveStatusOption?.value)
                    }
                    className="ms-3"
                  />{" "}
                  Deactive
                </div>
              </div>
              {errors.settingsStatusId ? (
                <small className="text-danger">{errors.settingsStatusId}</small>
              ) : null}
            </FormGroup>

            <div className="text-end mt-3">
              <Button color="primary" type="submit">
                <i className="fa fa-save me-1" aria-hidden="true" />
                {isEditMode ? "Update" : "Save"}
              </Button>
            </div>
          </Form>
        )}
      </ModalBody>
    </Modal>
  );
};

AddEditNotificationSettingsModal.propTypes = {
  notificationSettingsAddEditModalOpen: PropTypes.bool,
  setNotificationSettingsAddEditModalOpen: PropTypes.func,
  itemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  getNotificationSettingsList: PropTypes.func,
};

export default AddEditNotificationSettingsModal;
